import React from "react";
import { MapPin, Phone, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT, buildWhatsAppLink } from "@/lib/contact";
import { LogoMark } from "@/components/Logo";

const LINKS = [
  { key: "concept", href: "#concept" },
  { key: "menus", href: "#menu" },
  { key: "events", href: "#events" },
  { key: "reviews", href: "#reviews" },
  { key: "location", href: "#location" },
];

export const Footer = () => {
  const { t } = useLang();
  const waLink = buildWhatsAppLink(t.whatsappMessage);
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#003366] text-[#F9F9F9] relative grain-overlay"
    >
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-[#D4AF37]" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <LogoMark size={56} variant="light" />
              <div>
                <div className="font-serif text-2xl text-[#D4AF37]">
                  Monte Sol
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">
                  Stone Steak House
                </div>
              </div>
            </div>
            <p className="mt-6 font-serif italic text-white/70 text-lg max-w-md leading-snug">
              {t.footer.tagline}
            </p>
            <p className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
              {t.footer.follow}
            </p>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold">
              {t.footer.quickLinks}
            </div>
            <ul className="mt-5 space-y-3">
              {LINKS.map((l) => (
                <li key={l.key}>
                  <a
                    href={l.href}
                    data-testid={`footer-nav-${l.key}`}
                    className="text-sm text-white/85 hover:text-[#D4AF37] transition-colors"
                  >
                    {t.nav[l.key]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold">
              {t.footer.contact}
            </div>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#D4AF37] mt-0.5" />
                <a
                  href={CONTACT.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-maps-link"
                  className="text-white/85 hover:text-[#D4AF37] transition-colors"
                >
                  {CONTACT.addressLine}
                  <br />
                  {CONTACT.addressPostal}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#D4AF37]" />
                <a
                  href={`tel:${CONTACT.phone}`}
                  data-testid="footer-phone-link"
                  className="text-white/85 hover:text-[#D4AF37] transition-colors"
                >
                  {CONTACT.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="h-4 w-4 text-[#D4AF37]" />
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="text-white/85 hover:text-[#D4AF37] transition-colors"
                >
                  WhatsApp · {t.nav.bookTable}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar — Agency branding MUST be present */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
          <div className="text-white/50">
            © {year} Monte Sol Stone Steak House · {t.footer.rights}
          </div>
          <div
            data-testid="footer-agency-credit"
            className="text-white/60"
          >
            {t.footer.madeBy}{" "}
            <a
              href={CONTACT.agencyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-agency-link"
              className="text-[#D4AF37] hover:text-white transition-colors font-semibold"
            >
              {t.footer.agency}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
